/// <reference types="google_interactive_media_ads_types" />

import { Deferred, Logger } from '@quickplay/foundation';
import { createAdsRequest, isRequestContextMatch, setAdsRenderingSettings, setImaSdkSettings } from '../../util/AdUtil';
import { AD_REQUEST_CONTEXT } from '../../../models/csai/AdRequestContext';
import { IMAAdRequest } from '../../../models/csai/IMAAdRequest';
import { ImaSdkSettings } from '../../../models/ImaSdkSettings';
import { AdsRenderingSettings } from '../../../models/AdsRenderingSettings';

export enum QPPrefetchState {
  FETCHING = 'FETCHING',
  READY = 'READY',
  FAILED = 'FAILED',
  COMPLETED = 'COMPLETED',
}

export interface QPPrefetchManagerConfig {
  adsLoader: google.ima.AdsLoader;
  adRequest: IMAAdRequest;
  adsRenderingSettings?: AdsRenderingSettings;
  imaSDK: typeof google.ima;
  videoElement: HTMLVideoElement;
  logger: Logger;
  imaSdkSettings?: ImaSdkSettings;
}

/**
 * Manages the lifecycle of the prefetched pre-roll AdsManager.
 *
 * Public API: fetchAdsManager().
 * After the ad break finishes, the manager recycles itself and prefetches the next one.
 */
export class QPPrefetchManager {
  private readonly adsLoader: google.ima.AdsLoader;
  private readonly adRequest: IMAAdRequest;
  private readonly adsRenderingSettings?: AdsRenderingSettings;
  private readonly imaSDK: typeof google.ima;
  private readonly videoElement: HTMLVideoElement;
  private readonly imaSdkSettings?: ImaSdkSettings;
  private readonly logger: Logger;

  private readonly requestContext = AD_REQUEST_CONTEXT.PREFETCH;

  /** The ready-to-use AdsManager, or null if none is cached. */
  private adsManager: google.ima.AdsManager | null = null;

  /** The in-flight ad request, or null if nothing is being fetched. */
  private pending: Deferred<any> | null = null;
  private state = QPPrefetchState.FETCHING;

  constructor(config: QPPrefetchManagerConfig) {
    this.adsLoader = config.adsLoader;
    this.adRequest = config.adRequest;
    this.adsRenderingSettings = config.adsRenderingSettings;
    this.imaSDK = config.imaSDK;
    this.videoElement = config.videoElement;
    this.imaSdkSettings = config.imaSdkSettings;
    this.logger = config.logger;

    this.startBackgroundPrefetch();
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Returns the prefetched AdsManager.
   * - If one is cached, it resolves immediately.
   * - If a request is in flight, it shares that request.
   * - Otherwise it starts a new request (this is also the retry path after a failure).
   */
  public fetchAdsManager(): Promise<google.ima.AdsManager | null> {
    if (this.adsManager) {
      this.logger.log('[QPPrefetchManager] Returning cached AdsManager');
      return Promise.resolve(this.adsManager);
    }

    return this.pending?.promise ?? this.requestNewAdsManager();
  }

  /**
   * Throws away the used AdsManager and prefetches a fresh one.
   * Does nothing if a fetch is already in progress.
   */
  public recyclePrefetchedAdsManager(): void {
    if (this.state === QPPrefetchState.FETCHING) {
      this.logger.log('[QPPrefetchManager] Prefetch is already in progress, recycle ignored.');
      return;
    }
    this.logger.log('[QPPrefetchManager] Recycling prefetched AdsManager');

    this.cancelPrefetch();
    this.startBackgroundPrefetch();
  }

  // ---------------------------------------------------------------------------
  // Prefetch lifecycle (private)
  // ---------------------------------------------------------------------------

  /**
   * Starts a prefetch without anyone awaiting it (used by the constructor and after recycling).
   * The failure is already logged in markPrefetchFailed(), so the rejection is swallowed here
   * to avoid an unhandled promise rejection.
   */
  private startBackgroundPrefetch(): void {
    this.fetchAdsManager().catch((error: unknown) => {
      this.logger.log('[QPPrefetchManager] Background prefetch did not failed:', error);
    });
  }

  private requestNewAdsManager(): Promise<google.ima.AdsManager | null> {
    const deferred = new Deferred<google.ima.AdsManager | null>();
    const userRequestContext = { requestContext: this.requestContext };

    this.pending = deferred;
    this.state = QPPrefetchState.FETCHING;
    this.logger.log(`[QPPrefetchManager] Requesting new AdsManager and Prefetch marked as FETCHING`);
    this.toggleLoaderListeners(true);

    try {
      if (this.imaSdkSettings && this.imaSDK?.settings) {
        setImaSdkSettings(this.imaSDK.settings, this.imaSdkSettings);
      }

      const imaRequest = createAdsRequest(this.imaSDK, this.adRequest);

      this.adsLoader.requestAds(imaRequest, userRequestContext);
    } catch (error: unknown) {
      this.markPrefetchFailed(error);
    }

    return deferred.promise;
  }

  /**
   * Called when the AdsManager has loaded.
   * Caches it, sets the state to READY, listens for the end of the ad break
   * and resolves whoever is waiting on fetchAdsManager().
   */
  private markPrefetchReady(adsManager: google.ima.AdsManager): void {
    this.toggleLoaderListeners(false);
    this.adsManager = adsManager;
    this.state = QPPrefetchState.READY;
    this.logger.log(`[QPPrefetchManager] Prefetch marked as READY`);
    this.toggleAdBreakListeners(adsManager, true);

    this.detachPendingRequest()?.resolve(adsManager);
  }

  /**
   * Called when the request or the AdsManager creation fails.
   * Logs the error, sets the state to FAILED and rejects the waiting promise.
   * The pending slot is cleared, so the next fetchAdsManager() retries.
   */
  private markPrefetchFailed(error: unknown): void {
    this.toggleLoaderListeners(false);
    this.state = QPPrefetchState.FAILED;
    this.logger.log(`[QPPrefetchManager] Prefetch marked as FAILED:`, error);

    this.detachPendingRequest()?.reject(error);
  }

  /**
   * Discards everything: removes all listeners, drops the cached AdsManager,
   * sets the state to COMPLETED and rejects any request still in flight.
   */
  private cancelPrefetch(): void {
    this.toggleLoaderListeners(false);
    this.toggleAdBreakListeners(this.adsManager, false);
    this.adsManager = null;
    this.state = QPPrefetchState.COMPLETED;
    this.logger.log(`[QPPrefetchManager] Prefetch marked as COMPLETED`);

    this.detachPendingRequest()?.reject(new Error('[QPPrefetchManager] Prefetch cancelled'));
  }

  /**
   * Removes the in-flight request from this manager and returns it, so it can be
   * settled exactly once and a stale response can never touch a newer request.
   */
  private detachPendingRequest(): Deferred<any> | null {
    const request = this.pending;

    this.pending = null;
    return request;
  }

  // ---------------------------------------------------------------------------
  // IMA event handlers
  // ---------------------------------------------------------------------------

  /**
   * AdsLoader fired ADS_MANAGER_LOADED.
   * Ignores events from other requests, builds the rendering settings, creates the
   * AdsManager on our video element and marks the prefetch as ready.
   */
  private readonly onAdsManagerLoaded = (event: google.ima.AdsManagerLoadedEvent): void => {
    if (!this.isEventForThisRequest(event)) {
      return;
    }

    try {
      const renderingSettings = setAdsRenderingSettings(
        new this.imaSDK.AdsRenderingSettings(),
        this.adsRenderingSettings,
        this.imaSDK,
      );

      renderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;

      const adsManager = event.getAdsManager(this.videoElement, renderingSettings);

      this.markPrefetchReady(adsManager);
    } catch (error) {
      this.markPrefetchFailed(error);
    }
  };

  /**
   * AdsLoader fired AD_ERROR.
   * Ignores errors from other requests, otherwise marks the prefetch as failed.
   */
  private readonly onAdsLoaderError = (event: google.ima.AdErrorEvent): void => {
    if (!this.isEventForThisRequest(event)) {
      return;
    }

    this.markPrefetchFailed(event?.getError?.() ?? event);
  };

  /**
   * AdsManager fired CONTENT_RESUME_REQUESTED.
   * The ad break is over, so the used manager is replaced with a new prefetch.
   */
  private readonly onAdBreakCompleted = (): void => {
    this.recyclePrefetchedAdsManager();
  };

  /**
   * True if the IMA event belongs to this manager's ad request.
   * The AdsLoader is shared, so events from other requests also arrive here.
   */
  private isEventForThisRequest(event: google.ima.AdErrorEvent | google.ima.AdsManagerLoadedEvent): boolean {
    return isRequestContextMatch(event?.getUserRequestContext?.(), this.requestContext);
  }

  // ---------------------------------------------------------------------------
  // Listener wiring
  // ---------------------------------------------------------------------------

  /** Adds (true) or removes (false) the ADS_MANAGER_LOADED and AD_ERROR listeners on the AdsLoader. */
  private toggleLoaderListeners(attach: boolean): void {
    const { AdsManagerLoadedEvent, AdErrorEvent } = this.imaSDK;
    const method = attach ? 'addEventListener' : 'removeEventListener';

    this.adsLoader[method](AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, this.onAdsManagerLoaded, false);
    this.adsLoader[method](AdErrorEvent.Type.AD_ERROR, this.onAdsLoaderError, false);
  }

  /** Adds (true) or removes (false) the end-of-ad-break listeners on the given AdsManager. */
  private toggleAdBreakListeners(adsManager: google.ima.AdsManager | null | undefined, attach: boolean): void {
    const type = google.ima.AdEvent.Type;

    if (!adsManager?.addEventListener || !adsManager?.removeEventListener || !type) {
      return;
    }

    const method = attach ? 'addEventListener' : 'removeEventListener';

    adsManager[method](type.CONTENT_RESUME_REQUESTED, this.onAdBreakCompleted, false);
  }
}
